import React, { useState, useEffect } from 'react';
import proptypes from 'prop-types';
import { LoadingButton } from '@one-thd/sui-atomic-components';
import { Cart } from '@one-thd/sui-icons';
import { useDomPath } from '@thd-nucleus/experience-context';
import { DisclaimerDrawer } from '@thd-olt-component-react/disclaimer-drawer';

const {
  array, bool, string, oneOfType, shape, func, number, node
} = proptypes;

const AddToCart = ({
  cartReqParams = {
    itemId: null,
    quantity: 1
  },
  cartOptions = {
    channel: 'desktop',
    host: 'https://www.homedepot.com',
    misship: false,
    paypal: false
  },
  disabled = false,
  loading = false,
  onAddToCartClick = () => {},
  children = null,
  showBOPISOverlay = false,
  onClick = () => {},
  onClickPassThrough = null,
  onSuccess = () => {},
  onFail = () => {},
  onError = null,
  fullWidth = true,
  showIcon = false,
  variant = 'primary',
  customATCButtonRenderFunction = null,
  useParentWindowATC = false,
  isSmallButton = false
}) => {
  const [openSubscribeDrawer, setToggleDrawer] = useState(false);
  useEffect(() => {
    if (!loading && typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to-cart.ready');
    }
  }, []);

  const [path, ref] = useDomPath();

  const onSubscribeClick = () => {
    setToggleDrawer(true);
  };

  const onDrawerClose = () => {
    setToggleDrawer(false);
  };

  const onATCClick = () => {
    if (typeof window !== 'undefined') {
      const windowToUse = useParentWindowATC ? window.top : window;

      windowToUse.LIFE_CYCLE_EVENT_BUS.trigger('NATIVE_APP.ADD_TO_CART', {
        quantity: cartReqParams.quantity
      });
      if (disabled && onError) {
        onError();
      } else if (onClickPassThrough) {
        onClickPassThrough(cartReqParams);
      } else if (!disabled) {
        if (onClick) {
          onClick();
        }
        if (showBOPISOverlay) {
          windowToUse.LIFE_CYCLE_EVENT_BUS.trigger('cart.select-store', {
            cartReqParams
          });
        } else {
          // setTimeout added to optimize the long running thread affecting the INP score
          setTimeout(() => {
            windowToUse.LIFE_CYCLE_EVENT_BUS.trigger('cart.add-to-cart', {
              cartReqParams,
              cartOptions,
              onAddToCartClick,
              onSuccess,
              onFail,
              path
            });
          }, 0);
        }
      }
    }
  };

  const onDrawerConfirm = () => {
    onATCClick();
    onDrawerClose();
  };

  if (customATCButtonRenderFunction) {
    return (
      <div data-component="AddToCart" ref={ref}>
        {customATCButtonRenderFunction({ onClick: onATCClick })}
      </div>
    );
  }

  return (
    <>
      <DisclaimerDrawer
        openDrawer={openSubscribeDrawer}
        onCloseClick={onDrawerClose}
        onIUnderstandClick={onDrawerConfirm}
        discount={cartReqParams?.subscription?.discountPercentage}
        channel={cartOptions?.channel}
      />
      <div data-component="AddToCart" ref={ref}>
        <LoadingButton
          variant={variant}
          disabled={disabled}
          startIcon={showIcon ? Cart : undefined}
          fullWidth={fullWidth}
          loading={loading}
          onClick={cartReqParams?.isSubscribeAndSaveOpted ? onSubscribeClick : onATCClick}
          style={isSmallButton ? { fontSize: '14px', height: '30px' } : undefined}
        >
          {children}
        </LoadingButton>
      </div>
    </>
  );
};

AddToCart.dataModel = {};
AddToCart.displayName = 'AddToCart';

AddToCart.propTypes = {
  cartOptions: shape({
    directCheckout: bool,
    host: string,
    misship: bool,
    paypal: bool
  }),
  cartReqParams: oneOfType([
    array,
    shape({
      attachedLabor: shape({
        attachedLaborSku: string
      }),
      fulfillmentLocation: string,
      fulfillmentMethod: string,
      itemId: string,
      quantity: number,
      service: shape({
        selected: bool,
        type: string
      }),
      subscriptions: shape({
        frequency: string
      }),
      warrantyItem: shape({
        itemId: string
      })
    })
  ]),
  children: oneOfType([string, node]),
  customATCButtonRenderFunction: func,
  disabled: bool,
  fullWidth: bool,
  loading: bool,
  onAddToCartClick: func,
  onClick: func,
  onClickPassThrough: func,
  onError: func,
  onFail: func,
  onSuccess: func,
  showBOPISOverlay: bool,
  showIcon: bool,
  variant: string,
  useParentWindowATC: bool,
  isSmallButton: bool
};

export { AddToCart };
